import React, { createContext, useContext, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth, functions } from '../config/firebase';
import { 
  User as FirebaseUser, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  GoogleAuthProvider, 
  signInWithPopup, 
  sendPasswordResetEmail, 
  ActionCodeSettings, 
  sendEmailVerification,
  signInAnonymously,
  linkWithCredential,
  EmailAuthProvider,
  linkWithPopup
} from 'firebase/auth';
import { UserSessionData, UserDisplaySettings } from '@mono-repo-starter/types';
import { logFirebaseEvent } from '@/lib/tracking';
import { toast as sonnerToast } from "sonner";
import { httpsCallable } from '@/lib/callableProxy';
import { fetchSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { useAuthStore } from '@/stores/authStore';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userSettings: UserDisplaySettings | null;
  isInitializing: boolean;
  isLoadingUserSettings: boolean;
  isAuthActionInProgress: boolean;
  isAnonymous: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, referralCode?: string | null) => Promise<void>;
  upgradeAnonymousAccount: (email: string, password: string) => Promise<void>;
  upgradeAnonymousWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  signInWithGoogle: (referralCode?: string | null) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  fetchUserSettings: (userToFetchFor?: FirebaseUser | null, referralCode?: string | null) => Promise<UserSessionData | null | void>; 
  switchWorkspace: (workspaceId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { 
    currentUser, userSettings, isInitializing, isLoadingUserSettings, isAuthActionInProgress,
    setCurrentUser, setUserSettings, setInitializing, setLoadingUserSettings, setIsAuthActionInProgress 
  } = useAuthStore();
  const shouldAutoSignInAnonymously =
    import.meta.env.MODE === 'development' || import.meta.env.VITE_ENABLE_ANONYMOUS_AUTH === 'true';
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        setInitializing(false);
      } else {
        if (!shouldAutoSignInAnonymously) {
          setCurrentUser(null);
          setInitializing(false);
          return;
        }
        try {
          // Auto sign-in anonymously if configured
          await signInAnonymously(auth);
        } catch (error) {
          const authError = error as { code?: string; message?: string };
          if (authError?.code === 'auth/configuration-not-found') {
            console.warn('[Auth] Anonymous auth is not enabled for this Firebase project. Continuing unauthenticated.');
          } else {
            console.error("[Auth] Anonymous sign-in failed", error);
          }
          setInitializing(false);
        }
      }
    });
    return unsubscribe;
  }, [setCurrentUser, setInitializing, shouldAutoSignInAnonymously]);

  const fetchUserSettings = useCallback(async (userToFetchFor?: FirebaseUser | null, referralCode?: string | null): Promise<UserSessionData | null | void> => {
    const targetUser = userToFetchFor || useAuthStore.getState().currentUser;
    if (!targetUser) {
      setUserSettings(null);
      setLoadingUserSettings(false);
      return;
    }
    
    setLoadingUserSettings(true);
    
    try {
      const getUserSettingsFn = httpsCallable<{ referralCode?: string | null }, { success: boolean; settings?: UserSessionData; error?: string }>(functions, 'getUserSettings');
      let resolvedSettings: UserSessionData | null = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const result = await getUserSettingsFn({ referralCode });
        if (result.data.success && result.data.settings) {
          resolvedSettings = result.data.settings;
          break;
        }
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
        }
      }

      if (resolvedSettings) {
        setUserSettings({ ...resolvedSettings });
        return resolvedSettings;
      } else {
        setUserSettings(null);
        return null;
      }
    } catch (error: any) {
      console.error('AuthContext: Failed to fetch user settings.', error);
      setUserSettings(null);
      throw error;
    } finally {
      setLoadingUserSettings(false);
    }
  }, [setUserSettings, setLoadingUserSettings]);

  useEffect(() => {
    if (isInitializing) return;
    if (isAuthActionInProgress) return;

    if (currentUser) {
      const shouldFetch = !userSettings || userSettings.userId !== currentUser.uid;
      if (shouldFetch) {
        fetchUserSettings(currentUser);
      } else {
        setLoadingUserSettings(false);
      }
      queryClient.prefetchQuery({ queryKey: ['subscriptionPlans'], queryFn: fetchSubscriptionPlans });
    } else {
      setUserSettings(null);
      setLoadingUserSettings(false);
    }
  }, [currentUser, isInitializing, isAuthActionInProgress, fetchUserSettings, queryClient, userSettings, setLoadingUserSettings]);

  const signIn = useCallback(async (email: string, password: string) => {
    setIsAuthActionInProgress(true);
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (userCredential.user) await fetchUserSettings(userCredential.user);
    } finally {
        setIsAuthActionInProgress(false);
    }
  }, [fetchUserSettings, setIsAuthActionInProgress]);

  const signUp = useCallback(async (email: string, password: string, referralCode?: string | null) => {
    setIsAuthActionInProgress(true);
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (userCredential.user) {
            await fetchUserSettings(userCredential.user, referralCode); 
        }
    } finally {
        setIsAuthActionInProgress(false);
    }
  }, [fetchUserSettings, setIsAuthActionInProgress]);

  const upgradeAnonymousAccount = useCallback(async (email: string, password: string) => {
    if (!currentUser || !currentUser.isAnonymous) throw new Error("No anonymous user to upgrade.");
    setIsAuthActionInProgress(true);
    try {
        const credential = EmailAuthProvider.credential(email, password);
        const result = await linkWithCredential(currentUser, credential);
        
        const actionCodeSettings: ActionCodeSettings = {
            url: `${window.location.origin}/studio`, // Redirect to Studio after verification
            handleCodeInApp: true,
        };
        await sendEmailVerification(result.user, actionCodeSettings);
        sonnerToast.success("Account saved! Verification email sent.");
        await fetchUserSettings(result.user);
    } catch (error: any) {
        if (error.code === 'auth/credential-already-in-use') {
            throw new Error("This email is already associated with another account. Please sign out and log in.");
        }
        throw error;
    } finally {
        setIsAuthActionInProgress(false);
    }
  }, [currentUser, fetchUserSettings, setIsAuthActionInProgress]);

  const upgradeAnonymousWithGoogle = useCallback(async () => {
    if (!currentUser || !currentUser.isAnonymous) throw new Error("No anonymous user to upgrade.");
    setIsAuthActionInProgress(true);
    try {
        const provider = new GoogleAuthProvider();
        const result = await linkWithPopup(currentUser, provider);
        sonnerToast.success("Account successfully linked with Google.");
        await fetchUserSettings(result.user);
    } catch (error: any) {
        if (error.code === 'auth/credential-already-in-use') {
             throw new Error("This Google account is already in use. Please sign out and log in.");
        }
        throw error;
    } finally {
        setIsAuthActionInProgress(false);
    }
  }, [currentUser, fetchUserSettings, setIsAuthActionInProgress]);

  const logout = useCallback(async () => {
    await signOut(auth);
    useAuthStore.persist.clearStorage();
    queryClient.clear(); 
    logFirebaseEvent('logout');
  }, [queryClient]);

  const signInWithGoogle = useCallback(async (referralCode?: string | null) => {
    setIsAuthActionInProgress(true);
    try {
        const userCredential = await signInWithPopup(auth, new GoogleAuthProvider());
        if (userCredential.user) await fetchUserSettings(userCredential.user, referralCode);
    } finally {
        setIsAuthActionInProgress(false);
    }
  }, [fetchUserSettings, setIsAuthActionInProgress]);

  const sendPasswordReset = useCallback((email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${window.location.origin}/auth/action`,
      handleCodeInApp: true,
    };
    return sendPasswordResetEmail(auth, email, actionCodeSettings);
  }, []);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const switchWorkspaceFn = httpsCallable<{ workspaceId: string }, { success: boolean; message: string }>(functions, 'switchActiveWorkspace');
    await switchWorkspaceFn({ workspaceId });
    await fetchUserSettings();
  }, [fetchUserSettings]);

  const stableFetchUserSettings = useCallback((userToFetchFor?: FirebaseUser | null, referralCode?: string | null) => {
    return fetchUserSettings(userToFetchFor || currentUser, referralCode);
  }, [currentUser, fetchUserSettings]);

  const value = useMemo(() => ({
    currentUser, 
    userSettings, 
    isInitializing, 
    isLoadingUserSettings,
    isAuthActionInProgress,
    isAnonymous: currentUser?.isAnonymous ?? false,
    signIn, 
    signUp, 
    upgradeAnonymousAccount,
    upgradeAnonymousWithGoogle,
    logout, 
    signInWithGoogle, 
    sendPasswordReset,
    fetchUserSettings: stableFetchUserSettings,
    switchWorkspace,
  }), [
    currentUser, userSettings, isInitializing, isLoadingUserSettings, isAuthActionInProgress,
    signIn, signUp, upgradeAnonymousAccount, upgradeAnonymousWithGoogle, logout, 
    signInWithGoogle, sendPasswordReset, stableFetchUserSettings, switchWorkspace
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
